/**
 * Площадки, где в тексте и кэф, и центы. Зеркало часто пишет 69.1 в поле
 * кэфа — это цена шара, не коэффициент 69. Иначе ROI +266% при живых 5%.
 */
import { Decimal } from 'decimal.js';

import type { Fork, ForkLeg } from './protocol';
import { isPreparedFeed2Id } from './feedContext';
import { isPolySource } from './payout';

/** Compatibility with ready._row in the deployed BetBurger collector:
 * odds already include its generic profit commission, while share is gross.
 * The serializer retains the original quote and fee in this exact suffix.
 * Restore the quote only when the numbers confirm that contract; the row
 * calculator then applies the actual PM share fee once. Never infer a fee
 * merely from disagreeing odds/share (which may instead be a stale quote).
 * Native CLOB enrichment supplies its own gross book/fee and takes precedence.
 */
function restoreProviderPolyQuote(leg: ForkLeg): ForkLeg {
  if (!isPolySource(leg.s) || leg.book?.length) return leg;
  const match = /^(.*) · (\d+\.\d{3}) на бирже − (\d+(?:\.\d+)?)%$/.exec(leg.name ?? '');
  if (!match) return leg;
  try {
    const gross = new Decimal(match[2]!);
    const fee = new Decimal(match[3]!);
    const price = new Decimal(leg.share ?? '0');
    if (!gross.gt(1) || !fee.gt(0) || !fee.lt(100) || !price.gt(0) || !price.lt(1)) return leg;
    const net = gross.minus(1).times(new Decimal(1).minus(fee.div(100))).plus(1);
    // Both serialized odds were rounded down to 3 dp; share to 4 dp.
    if (net.minus(leg.odds).abs().gt('.001') || new Decimal(1).div(gross).minus(price).abs().gt('.001')) return leg;
    return { ...leg, odds: match[2]!, name: match[1]! };
  } catch {
    return leg;
  }
}

/** Run before alignFork can replace the gross share price with 1/net odds. */
export function restoreProviderPolyFork(fork: Fork): Fork {
  if (!isPreparedFeed2Id(fork.id) || !fork.legs) return fork;
  const legs = fork.legs.map((leg, i) =>
    fork.o?.[i] !== undefined && fork.o[i] !== leg.odds ? leg : restoreProviderPolyQuote(leg));
  return legs.some((leg, i) => leg !== fork.legs![i])
    ? { ...fork, legs, o: legs.map((leg, i) => leg !== fork.legs![i] ? leg.odds : (fork.o?.[i] ?? leg.odds)) } : fork;
}

const SHARE_VENUES = new Set([
  'polymarket',
  'poly',
  'predictfun',
  'kalshi',
  'opinion',
  'myriad',
  'limitless',
  'sxbet',
  'bxyz',
  'sharpxch',
]);

export const SHARE_ODDS_CEILING = 25;

export function isShareVenue(source: string): boolean {
  const code = source.trim().toLowerCase();
  if (code === 'poly' || code.startsWith('polymarket')) return true;
  return SHARE_VENUES.has(code);
}

export function isShareLeg(leg: {
  s?: string;
  unit?: string | null;
  book?: readonly unknown[] | null;
}): boolean {
  return (
    leg.unit === 'share' ||
    Boolean(leg.book && leg.book.length > 0) ||
    isShareVenue(leg.s ?? '')
  );
}

export function lookLikeShareCents(odds: number): boolean {
  return Number.isFinite(odds) && odds >= SHARE_ODDS_CEILING && odds < 100;
}

export function paintShareCents(leg: ForkLeg, cents: string): ForkLeg | null {
  try {
    const n = new Decimal(cents.trim().replace(',', '.'));
    if (!n.isFinite() || !n.gt(0)) return null;
    const price = n.div(100);
    if (price.lte(0) || price.gte(1)) return null;
    const odds = new Decimal(1).div(price).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
    const share = price.toDecimalPlaces(3, Decimal.ROUND_UP).toString();
    return { ...leg, unit: 'share', odds, share };
  } catch {
    return null;
  }
}

function impliedSum(fork: Fork): number | null {
  const legs = fork.legs ?? [];
  if (legs.length < 2) return null;
  let sum = 0;
  for (const leg of legs) {
    const n = Number(leg.odds);
    if (!Number.isFinite(n) || !(n > 1)) return null;
    sum += 1 / n;
  }
  return sum;
}

/** 69.1 + 4.03: 0.26 → 0.94 — берём центы. 50 + 1.03: настоящий лонгшот, не трогаем. */
export function healShareFork(fork: Fork): Fork {
  if (fork.legs === undefined) return fork;
  fork = restoreProviderPolyFork(fork);
  let changed = false;
  const legs = fork.legs!.map((leg) => {
    if (!isShareLeg(leg)) return leg;
    const n = Number(leg.odds);
    if (!lookLikeShareCents(n)) return leg;
    const next = paintShareCents(leg, n.toFixed(1));
    if (next === null || next.odds === leg.odds) return leg;
    changed = true;
    return next;
  });
  if (!changed) return fork;
  const healed = { ...fork, legs, o: legs.map((one) => one.odds) };
  const before = impliedSum(fork);
  const after = impliedSum(healed);
  if (after === null) return fork;
  if (before !== null && Math.abs(after - 1) >= Math.abs(before - 1)) return fork;
  return healed;
}

/** Замок биржи — центы. 69.1 как кэф даёт +266%; 2.000 в замке — старый кэф, не 2¢. */
export function oddsFromShareLock(raw: string): string {
  try {
    const n = new Decimal(raw.trim().replace(',', '.'));
    if (!n.isFinite() || !n.gt(0)) return raw;
    if (n.lt(10)) {
      const asOdds = new Decimal(100).div(n);
      if (asOdds.gte(SHARE_ODDS_CEILING)) {
        return n.toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
      }
    }
    return new Decimal(100).div(n).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
  } catch {
    return raw;
  }
}
